import { Avatar, AvatarFallback } from '@/app/_components/core/avatar';
import { RespImage } from '@/app/_components/core/resp-image';
import type { QuoteItem, QuoteCardVariant } from '@/app/_components/shared/quote-section/quote-section.types';
import { transformSrcsetCandidates } from '@/app/_lib/image-art-direction';

interface QuoteCardProps {
  item: QuoteItem;
  variant?: QuoteCardVariant;
}

function getInitials(name: string): string {
  if (!name || name.trim() === '') {
    return '??';
  }
  return name
    .trim()
    .split(' ')
    .filter((n) => n.length > 0)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Avatar: 48px fixed size
const AVATAR_SIZES = '48px';

// Image variant: 3 cols desktop, 2 cols tablet, 1 col mobile
const IMAGE_SIZES =
  '(min-width: 1024px) 410px, (min-width: 768px) 360px, calc(100vw - 32px)';

function AvatarVariant({
  item,
  srcsetCandidates,
}: {
  item: QuoteItem;
  srcsetCandidates: { imageUrl: string; width: number; height: number }[];
}) {
  // name is a required string in quoteItemSchema — no fallback needed.
  const fallbackLabel = item.name;

  return (
    <div className="flex flex-col gap-4">
      {/* Avatar + Name + Position */}
      <div className="flex items-start gap-4">
        <Avatar size="lg" className="shrink-0">
          {srcsetCandidates.length > 0 ? (
            <RespImage
              srcsetCandidates={srcsetCandidates}
              title={item.image?.title}
              alt={item.image?.altText ?? fallbackLabel}
              sizes={AVATAR_SIZES}
              className="size-full object-cover"
            />
          ) : (
            <AvatarFallback size="lg">{getInitials(item.name)}</AvatarFallback>
          )}
        </Avatar>
        <div className="flex flex-col">
          <p className="text-base/normal  font-semibold text-foreground">
            {item.name}
          </p>
          {item.position && (
            <p className="text-base/normal  text-muted-foreground">
              {item.position}
            </p>
          )}
        </div>
      </div>
      {/* Quote Text */}
      <p className="text-base/normal  text-muted-foreground">
        &ldquo;{item.quote}&rdquo;
      </p>
    </div>
  );
}

function ImageVariant({
  item,
  srcsetCandidates,
}: {
  item: QuoteItem;
  srcsetCandidates: { imageUrl: string; width: number; height: number }[];
}) {
  // name is a required string in quoteItemSchema — no fallback needed.
  const fallbackLabel = item.name;

  return (
    <div className="flex flex-col gap-4">
      {/* Large Image (4:3 aspect ratio) */}
      {srcsetCandidates.length > 0 && (
        <div className="aspect-4/3 w-full overflow-hidden rounded-quote-section-media-radius">
          <RespImage
            srcsetCandidates={srcsetCandidates}
            alt={item.image?.altText ?? fallbackLabel}
            title={item.image?.title}
            sizes={IMAGE_SIZES}
            className="size-full object-cover"
          />
        </div>
      )}
      {/* Name + Position */}
      <div className="flex flex-col">
        <p className="text-base/normal  font-semibold text-foreground">
          {item.name}
        </p>
        {item.position && (
          <p className="text-base/normal  text-muted-foreground">
            {item.position}
          </p>
        )}
      </div>
      {/* Quote Text */}
      <p className="text-base/normal  text-muted-foreground">
        &ldquo;{item.quote}&rdquo;
      </p>
    </div>
  );
}

export function QuoteCard({ item, variant = 'avatar' }: QuoteCardProps) {
  const candidates = item.image?.srcsetCandidates;
  const hasSrcset =
    candidates &&
    (Array.isArray(candidates)
      ? candidates.length > 0
      : Object.keys(candidates).length > 0);

  // No `&& candidates`: TS narrows candidates through the aliased hasSrcset
  // condition itself these days.
  const { srcsetCandidates } = hasSrcset
    ? transformSrcsetCandidates(candidates)
    : { srcsetCandidates: [] };

  if (variant === 'image') {
    return <ImageVariant item={item} srcsetCandidates={srcsetCandidates} />;
  }

  return <AvatarVariant item={item} srcsetCandidates={srcsetCandidates} />;
}
